import type { CaptureScope } from './types'

/** Local UI gate only. Decoding does not verify a JWT or replace server authorization. */
export interface CaptureIdentity {
  actorId: string
  sessionVersion: number
  tokenExpiresAt: number
}

export interface CaptureAuthInput {
  userId: string | null | undefined
  token: string | null | undefined
  isMasquerading: boolean
  masqueradeOriginalUserId?: string | null
}

function decodePart(value: string): Record<string, unknown> | null {
  if (!value || !/^[A-Za-z0-9_-]+$/.test(value)) return null
  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
    const bytes = Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')), char => char.charCodeAt(0))
    const parsed: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes))
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null
  } catch {
    return null
  }
}

/**
 * Expired direct tokens may identify an already prepared local session: capture
 * expiry is the server-provided session end, not the JWT expiry. Network writes
 * still require the server to authenticate the bearer and current permissions.
 * Never persist this result as an independent authorization grant.
 */
export function readCaptureIdentity(auth: CaptureAuthInput): CaptureIdentity | null {
  if (!auth.userId || !auth.token || auth.isMasquerading || auth.masqueradeOriginalUserId) return null
  const parts = auth.token.split('.')
  if (parts.length !== 3 || !parts[2] || !/^[A-Za-z0-9_-]+$/.test(parts[2])) return null
  const header = decodePart(parts[0] || '')
  const claims = decodePart(parts[1] || '')
  if (header?.alg !== 'RS256' || claims?.sessionKind !== 'direct' || claims.id !== auth.userId) return null
  const version = claims.sv ?? 0
  if (!Number.isSafeInteger(version) || Number(version) < 0 ||
      !Number.isSafeInteger(claims.exp) || Number(claims.exp) <= 0 ||
      !Number.isSafeInteger(Number(claims.exp) * 1000)) return null
  return { actorId: auth.userId, sessionVersion: Number(version), tokenExpiresAt: Number(claims.exp) * 1000 }
}

export function captureIdentityMatchesScope(identity: CaptureIdentity | null, scope: CaptureScope): boolean {
  return !!identity && identity.actorId === scope.realActorId && identity.actorId === scope.effectiveActorId
}

/** This is a local preflight; true does not mean the server will accept the token. */
export function captureTokenIsCurrent(identity: CaptureIdentity | null, now = Date.now()): boolean {
  return !!identity && Number.isFinite(now) && now < identity.tokenExpiresAt
}

/** Check both boundaries. Invalid/missing dates fail closed; no clock-skew grace is invented. */
export function preparedCaptureWindowIsOpen(preparedAt: string, sessionEndsAt: string, now = Date.now()): boolean {
  const offset = /(?:Z|[+-]\d{2}:\d{2})$/i
  if (!offset.test(preparedAt) || !offset.test(sessionEndsAt) || !Number.isFinite(now)) return false
  const start = Date.parse(preparedAt)
  const end = Date.parse(sessionEndsAt)
  return Number.isFinite(start) && Number.isFinite(end) && start < end && now >= start && now < end
}

/** Diagnostic categories only; never expose token contents or relax the gate. */
export function captureIdentityIssue(auth: CaptureAuthInput): 'signedOut' | 'masquerade' | 'legacy' | 'accountMismatch' | 'invalid' | null {
  if (!auth.userId || !auth.token) return 'signedOut'
  if (auth.isMasquerading || auth.masqueradeOriginalUserId) return 'masquerade'
  const claims = decodePart(auth.token.split('.')[1] || '')
  if (!claims) return 'invalid'
  if (claims.sessionKind === 'masquerade') return 'masquerade'
  if (claims.id !== auth.userId) return 'accountMismatch'
  if (claims.sessionKind === undefined || claims.sessionKind === 'legacy') return 'legacy'
  return readCaptureIdentity(auth) ? null : 'invalid'
}
